// src/pages/IrsConfirmation.js
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fmtDateTimeUS } from '../utils/dates';
import HeaderLogged from '../components/HeaderLogged';
import Footer from '../components/Footer';
import { isPrefileActive } from '../utils/prefile';
import { useWizardFormId } from '../utils/wizardForm';

function IrsConfirmation() {
  const navigate = useNavigate();
  // Номер формы — из адреса этой вкладки (см. utils/wizardForm.js): иначе после
  // оплаты страница опрашивала бы статус чужой формы из старого localStorage.
  const formId = useWizardFormId();
  const hasRun = useRef(false);
  const [irsSubmissionId, setIrsSubmissionId] = useState('');
  const [submissionDate, setSubmissionDate] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [prefiled, setPrefiled] = useState(false);
  const [queued, setQueued] = useState(false);
  const [paymentOption, setPaymentOption] = useState('');

  const steps = [
    { id: 1, label: 'Tax & Form' },
    { id: 2, label: 'Vehicles Tax' },
    { id: 3, label: 'IRS Payment Options' },
    { id: 4, label: 'Form Summary' },
    { id: 5, label: 'Payment & Submission' },
    { id: 6, label: 'IRS Confirmation' },
  ];
  const currentStep = 6;

  const POLL_INTERVAL_MS = 1500;  
  const POLL_TIMEOUT_MS  = 60000; 

  useEffect(() => {
    if (!formId) return; // useWizardFormId уводит на страницу компании
    if (hasRun.current) return;
    hasRun.current = true;

    const token = localStorage.getItem('token');
    if (!token) {
      setError('No auth token');
      return;
    }

    let cancelled = false;     
    let timerId = null;        
    const startTs = Date.now();  

    setError('');             
    setAlreadySubmitted(false);  
    setSubmitting(true);       

    const poll = async () => {   
      if (cancelled) return;

      try {
        const res = await fetch(`/api/form2290/${formId}/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to load form status');
        const formData = await res.json();
        setPaymentOption(formData?.irs_payment_option || '');
        const status = formData?.status;

        // Pre-filed returns (tax year 2026-2027) are intentionally NOT submitted
        // to the IRS now — they are queued and submitted automatically after
        // July 1, 2026. tax_period is set well before payment, so this branch is
        // race-free vs. the background task that flips status to 'prefiled'.
        // After July 1 the season is open: 2026-2027 returns submit immediately
        // (status 'send'/'complete'), so isPrefileActive() is false and we fall
        // through to the normal "submitted" flow.
        if (isPrefileActive(formData?.tax_period) || status === 'prefiled') {
          if (cancelled) return;
          setPrefiled(true);
          setSubmitting(false);
          setSubmissionDate(fmtDateTimeUS(new Date()));
          return;
        }

        // Аварийная пауза: форма принята и оплачена, но отправка в IRS отложена.
        // Её отправит фоновая задача, как только IRS снова будет доступен.
        if (formData?.irs_queued_at) {
          if (cancelled) return;
          setQueued(true);
          setSubmitting(false);
          setSubmissionDate(fmtDateTimeUS(new Date()));
          return;
        }

        if (status === 'send' || status === 'complete') {
          if (cancelled) return;
          setAlreadySubmitted(true);
          setSubmitting(false);
          const saved = localStorage.getItem('currentIrsSubmissionId');
          if (saved) setIrsSubmissionId(saved);
          setSubmissionDate(fmtDateTimeUS(new Date()));
          return; 
        }
      } catch (e) {
        
      }

      const elapsed = Date.now() - startTs;
      if (elapsed >= POLL_TIMEOUT_MS) {
        if (cancelled) return;
        setSubmitting(false);
        setAlreadySubmitted(false);
        setError(
          'There was an error submitting your form. Please contact support@2290pay.org for assistance. We will respond within 24–48 hours.'
        );
        return; 
      }

      timerId = setTimeout(poll, POLL_INTERVAL_MS);
    };

    timerId = setTimeout(poll, 0);

    return () => {
      cancelled = true;        
      if (timerId) clearTimeout(timerId);
    };
  }, [formId]);

  const selectedPaymentOption = localStorage.getItem('selectedPaymentOption');

  return (
    <div className="flex flex-col min-h-screen bg-[#E4F2FF]">
      <HeaderLogged />

      {/* Progress bar */}
      <div className="bg-white shadow-md">
        <div className="max-w-6xl mx-auto px-4 py-3 flex flex-wrap justify-center gap-3">
          {steps.map(step => {
            const isCurrent = step.id === currentStep;
            const isNext    = step.id === currentStep + 1;
            const isCompleted = step.id < currentStep;

            const baseClasses = 'text-sm font-medium px-3 py-1 border';
            const visibleClasses = isCurrent || isNext || isCompleted
              ? 'block'
              : 'hidden sm:block';

            const colorClasses = isCurrent
              ? 'bg-[#0F2C5E] text-white border-[#0F2C5E]'
              : isCompleted
                ? 'bg-green-100 text-green-700 border-green-200'
                : isNext
                  ? 'bg-[#E4E8F0] text-[#0F2C5E] border-[#E4E8F0]'
                  : 'bg-gray-100 text-gray-600 border-gray-200';

            return (
              <div
                key={step.id}
                className={`${baseClasses} ${visibleClasses} ${colorClasses}`}
              >
                {step.label}
              </div>
            );
          })}
        </div>
      </div>

      {/* Main content */}
      <main className="flex-1 py-6 px-4 flex justify-center">
        <div className="w-full max-w-4xl bg-white border border-gray-200 shadow-md p-6">
          <h1 className="text-2xl font-semibold text-[#0F2C5E] mb-4">
            IRS Confirmation
          </h1>

          {/* Ошибки и уведомления */}
          {error && (
            <p className="text-red-600 mb-4">{error}</p>
          )}

          {submitting && (
            <p className="text-gray-600 mb-4">Submitting to IRS…</p>
          )}

          {queued && !submitting && (
            <div className="bg-amber-50 border border-amber-300 rounded-md p-4 mb-6">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-amber-900 font-semibold mb-2">
                    Your return has been received and is queued for the IRS.
                  </p>
                  <p className="text-gray-700 mb-2">
                    The IRS e-file system is experiencing technical difficulties on their
                    side right now, so we are holding your return instead of risking a
                    failed transmission. It will be sent automatically as soon as the IRS
                    is available again, and your stamped Schedule&nbsp;1 will be emailed to
                    you. Your payment is complete and no further action is needed.
                  </p>
                  {submissionDate && (
                    <p className="text-gray-700 mb-2">
                      Received: <span className="font-medium">{submissionDate}</span>
                    </p>
                  )}
                  <p className="text-gray-600 text-sm">
                    Questions? Contact{' '}
                    <a href="mailto:support@2290pay.org" className="text-blue-700 underline">
                      support@2290pay.org
                    </a>
                    .
                  </p>
                </div>
              </div>
            </div>
          )}

          {prefiled && !submitting && (
            <div className="bg-green-50 border border-green-300 rounded-md p-4 mb-6">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-green-800 font-semibold mb-2">
                    Your 2290 return has been pre-filed for tax year 2026–2027.
                  </p>
                  <p className="text-gray-700 mb-2">
                    The IRS does not begin accepting e-filed Form 2290 returns for this
                    tax year until <span className="font-medium">July 1, 2026</span>. We
                    will automatically submit your return to the IRS on July 1, 2026, and
                    your stamped Schedule&nbsp;1 will be issued shortly after. No further
                    action is needed.
                  </p>
                  {submissionDate && (
                    <p className="text-gray-700 mb-2">
                      <span className="font-medium">Pre-filed on:</span>{' '}
                      {submissionDate}
                    </p>
                  )}
                  <p className="text-gray-700">
                    You can review your pre-filed return anytime from your dashboard.
                    A confirmation email is on its way.
                  </p>
                </div>
              </div>
            </div>
          )}

          {alreadySubmitted && !submitting && (
            <>
              <p className="text-gray-700 mb-2">
                Form was submitted to the IRS.
              </p>
              {irsSubmissionId && (
                <p className="text-gray-700 mb-2">
                  <span className="font-medium">IRS Submission ID:</span>{' '}
                  {irsSubmissionId}
                </p>
              )}
              {submissionDate && (
                <p className="text-gray-700 mb-4">
                  <span className="font-medium">Submission Date:</span>{' '}
                  {submissionDate}
                </p>
              )}
              <p className="text-gray-700 mb-6">
                You will receive an email confirmation shortly. If you need to
                review your submission, you can access it from your dashboard.
              </p>
            </>
          )}

          <div className="flex justify-end mt-6">
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-[#0F2C5E] text-white px-6 py-2 hover:bg-[#0F2C5E]/80 transition text-sm"
            >
              Go to Dashboard
            </button>
          </div>
          <div className='flex justify-end mt-2'>
            {paymentOption === 'CCDC' && (
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-md text-gray-800 mb-6">
                <strong className="block mb-1 text-[#0F2C5E]">Credit / Debit Card Payment</strong>
                Please complete your IRS payment at{' '}
                <a href="https://www.pay1040.com/payment/business" target="_blank" rel="noreferrer" className="text-blue-600 underline">
                  https://www.pay1040.com/payment/business
                </a>.
              </div>
            )}

            {paymentOption === 'EFTPS' && (
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-md text-gray-800 mb-6">
                <strong className="block mb-1 text-[#0F2C5E]">EFTPS Payment Required</strong>
                Please pay at{' '}
                <a href="https://www.eftps.gov" target="_blank" rel="noreferrer" className="text-blue-600 underline">
                  https://www.eftps.gov
                </a>.
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default IrsConfirmation;
